/**
 * Navigation Configuration
 * Centralized hardcoded navigation structure for the sidebar
 * This eliminates prop drilling and provides a single source of truth
 */

import {
  Bell,
  BellElectric,
  BookText,
  Building2,
  ClipboardList,
  Folder,
  LayoutDashboard,
  Link,
  ListTodo,
  MessageSquareMore,
  Users,
} from 'lucide-react'

import { ClockRewind } from '@/components/Icons'
import { ERoleType } from '@/features/user-management/constants'
import { ROUTES } from '@/utils/routes.ts'

import {
  SidebarGroupConfig,
  SidebarItemConfig,
  SidebarSectionConfig,
} from '../types.ts'

export const NAVIGATION_SECTIONS: SidebarSectionConfig[] = [
  // My Work Section
  {
    title: 'sidebar.section.myWork',
    items: [
      {
        id: 'notifications',
        label: 'sidebar.item.notifications',
        icon: Bell,
        path: ROUTES.UNAUTHORIZED,
        badge: 5,
        roles: [
          ERoleType.ORGANIZATION_ADMIN,
          ERoleType.PROJECT_LEAD,
          ERoleType.TEAM_MEMBER,
          ERoleType.REVIEWER,
          ERoleType.VIEWER,
        ],
      },
      {
        id: 'my-work',
        label: 'sidebar.item.myWork',
        icon: ListTodo,
        path: ROUTES.MY_WORK,
        roles: [
          ERoleType.ORGANIZATION_ADMIN,
          ERoleType.PROJECT_LEAD,
          ERoleType.TEAM_MEMBER,
          ERoleType.REVIEWER,
          ERoleType.VIEWER,
        ],
      },
    ],
  },
  // Admin Section
  {
    title: 'sidebar.section.admin',
    items: [
      {
        id: 'user-management',
        label: 'sidebar.item.userManagement',
        path: ROUTES.USER_MANAGEMENT,
        icon: Users,
        roles: [ERoleType.ORGANIZATION_ADMIN, ERoleType.PROJECT_LEAD],
      },
      {
        id: 'reminder',
        label: 'sidebar.item.reminder',
        path: ROUTES.WORKFLOW_CONFIG,
        icon: BellElectric,
        roles: [ERoleType.ORGANIZATION_ADMIN],
      },
      {
        id: 'user-mapping',
        label: 'sidebar.subItem.userMapping',
        path: ROUTES.USER_MAPPING,
        icon: Link,
        roles: [ERoleType.ORGANIZATION_ADMIN],
      },
      {
        id: 'comment-templates',
        label: 'sidebar.item.commentTemplates',
        path: ROUTES.COMMENT_TEMPLATES,
        icon: BookText,
        roles: [ERoleType.ORGANIZATION_ADMIN, ERoleType.PROJECT_LEAD],
      },
      {
        id: 'history',
        label: 'sidebar.item.history',
        path: ROUTES.HISTORY,
        icon: ClockRewind,
        roles: [ERoleType.ORGANIZATION_ADMIN],
      },
    ],
  },
  // Analytics Section
  {
    title: 'sidebar.section.analytics',
    items: [
      {
        id: 'dashboard',
        label: 'sidebar.item.dashboard',
        icon: LayoutDashboard,
        path: ROUTES.DASHBOARD,
        roles: [ERoleType.ORGANIZATION_ADMIN, ERoleType.PROJECT_LEAD],
      },
    ],
  },

  // TFL Review Section
  {
    title: 'sidebar.section.organizationManagement',
    items: [
      {
        id: 'company',
        label: 'sidebar.subItem.company',
        path: ROUTES.COMPANY,
        icon: Building2,
        roles: [ERoleType.ORGANIZATION_ADMIN],
      },
      {
        id: 'project',
        label: 'sidebar.subItem.project',
        path: ROUTES.PROJECT,
        icon: Folder,
        roles: [
          ERoleType.ORGANIZATION_ADMIN,
          ERoleType.PROJECT_LEAD,
          ERoleType.TEAM_MEMBER,
        ],
      },
      {
        id: 'deliverable-set',
        label: 'sidebar.subItem.deliverableSet',
        path: ROUTES.DELIVERABLE_SET,
        icon: ClipboardList,
        roles: [
          ERoleType.ORGANIZATION_ADMIN,
          ERoleType.PROJECT_LEAD,
          ERoleType.TEAM_MEMBER,
        ],
      },
      {
        id: 'comment',
        label: 'sidebar.subItem.comment',
        path: ROUTES.COMMENT,
        icon: MessageSquareMore,
        roles: [
          ERoleType.ORGANIZATION_ADMIN,
          ERoleType.PROJECT_LEAD,
          ERoleType.TEAM_MEMBER,
        ],
      },
    ],
  },
]

export const getNavigationSections = (): SidebarSectionConfig[] => {
  return NAVIGATION_SECTIONS
}
/**
 * Helper to find a navigation item by path
 * Useful for breadcrumbs or active state detection
 */
export const findNavItemByPath = (
  path: string
): {
  section: SidebarSectionConfig
  item: SidebarItemConfig | SidebarGroupConfig['children'][number]
} | null => {
  for (const section of NAVIGATION_SECTIONS) {
    for (const item of section.items) {
      if ('children' in item) {
        const child = item.children.find((c) => c.path === path)
        if (child) return { section, item: child }
      } else if (item.path === path) {
        return { section, item }
      }
    }
  }
  return null
}
